"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FileText, Plus, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useActiveOrg } from "@/features/active-org/_context/ActiveOrgContext";

import { useEstimator } from "../../_context/EstimatorContext";
import { useEstimatorSources } from "../../_context/EstimatorSourcesContext";
import { useAttendeeFile } from "../../_hooks/useAttendeeFile";
import { useRemovePlace } from "../../_hooks/useRemovePlace";
import { resolvePlaces } from "../../_logic/place-catalog";
import { parsePastedTravelers, type PlaceOption } from "../../_logic/places";
import { ATTENDEE_FILE_ACCEPT } from "../../_logic/read-attendee-file";
import type { Origin } from "../../_logic/types";
import { newWhoDraft, type WhoDraft } from "../../_logic/trip-drafts";
import { cityKey, knownTravelers, originKey, travelerCount, unknownTravelers } from "../../_logic/workspace";
import { AttendeeUploadHint } from "../attendees/AttendeeUploadHint";
import { TravelerRow } from "./TravelerRow";

const people = (count: number) => `${count.toLocaleString("en-US")} ${count === 1 ? "person" : "people"}`;

/**
 * The Who popover: one editable row per home city ([city] [− count +] [remove]). A city must be
 * picked from real places; rows stay editable. "Add home city" appends a row, Enter in the last
 * row adds another and Backspace on an empty one removes it. Pasting "San Diego 8, Austin 7" (or
 * a column from a spreadsheet) adds every city it can find. An attendee list fills the rows too.
 * People counted but not placed (an event's headcount, or "Just a headcount") sit in one more row,
 * "Where are they coming from?": picking a city turns them into that city's row, and each city
 * added takes its people from them, so the total stays put.
 * @param props.focusCity - The home city to focus on opening, e.g. the one the bar shows. People
 *   without a home city take focus first, since their empty field is what needs filling.
 * @returns The editor.
 * @throws Never.
 */
export const WhoEditor = ({
  focusCity,
  drafts,
  setDrafts,
}: {
  focusCity?: string | null;
  /** Unpicked rows, kept by the trip bar so closing the popover doesn't drop them. */
  drafts: WhoDraft[];
  setDrafts: React.Dispatch<React.SetStateAction<WhoDraft[]>>;
}) => {
  // Hooks
  const { workspace, edit } = useEstimator();
  const { upload, reading } = useAttendeeFile();
  const { removeOrigin } = useRemovePlace();
  const orgId = useActiveOrg().activeOrg?.id ?? null;
  const sources = useEstimatorSources();
  const fileInput = useRef<HTMLInputElement>(null);

  // States
  // An empty Who starts with one row to type into.
  const [starter] = useState<WhoDraft | null>(() =>
    drafts.length === 0 && workspace.origins.length === 0 && unknownTravelers(workspace) === 0 ? newWhoDraft() : null
  );
  // Null focuses the row of people without a home city: the empty field the pricing hints send planners to.
  const [focusId, setFocusId] = useState<string | null>(() => {
    if (unknownTravelers(workspace) > 0) return null;
    return focusCity ? `city:${cityKey(focusCity)}` : (drafts[0]?.id ?? starter?.id ?? null);
  });
  const [notFound, setNotFound] = useState<string[]>([]);
  const [resolving, setResolving] = useState(false);
  const [frequent, setFrequent] = useState<PlaceOption[]>([]);

  // Derived
  const unknown = unknownTravelers(workspace);
  const known = knownTravelers(workspace);
  const total = travelerCount(workspace);

  // Handlers
  const setOrigins = (origins: Origin[]) => edit({ type: "setOrigins", origins }, "user");
  const pinAirport = (place: PlaceOption) =>
    place.codes[0] && edit({ type: "setAirport", city: place.city, code: place.codes[0] }, "user");

  const addDraft = () => {
    const draft = newWhoDraft();
    setDrafts((current) => [...current, draft]);
    setFocusId(draft.id);
  };

  const pickFor = (index: number, place: PlaceOption) => {
    setOrigins(workspace.origins.map((origin, at) => (at === index ? { ...origin, city: place.city } : origin)));
    pinAirport(place);
  };

  const pickDraft = (draft: WhoDraft, place: PlaceOption) => {
    setOrigins([...workspace.origins, { city: place.city, count: draft.count }]);
    pinAirport(place);
    setDrafts((current) => current.filter((entry) => entry.id !== draft.id));
  };

  const pasteList = (draftId: string | null) => (text: string) => {
    const entries = parsePastedTravelers(text);
    if (entries.length === 0) return false;
    setResolving(true);
    void resolvePlaces(entries.map((entry) => entry.city)).then((places) => {
      setResolving(false);
      const found = entries
        .map((entry, index) => ({ entry, place: places[index] }))
        .filter((pair): pair is { entry: (typeof entries)[number]; place: PlaceOption } => pair.place !== null);
      setNotFound(entries.filter((_, index) => places[index] === null).map((entry) => entry.city));
      if (found.length === 0) return;
      setOrigins([
        ...workspace.origins,
        ...found.map(({ entry, place }) => ({ city: place.city, count: entry.count })),
      ]);
      found.forEach(({ place }) => pinAirport(place));
      if (draftId) setDrafts((current) => current.filter((entry) => entry.id !== draftId));
    });
    return true;
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (await upload(file)) setDrafts([]);
  };

  // Effects
  useEffect(() => {
    if (starter) setDrafts((current) => (current.length ? current : [starter]));
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    void sources
      .frequentHomeCities(orgId)
      .catch(() => [])
      .then((places) => !cancelled && setFrequent(places));
    return () => {
      cancelled = true;
    };
  }, [orgId, sources]);

  // Render
  const quickPicks = frequent.length
    ? {
        heading: "Frequent home cities",
        options: frequent.filter(
          (place) => !workspace.origins.some((origin) => cityKey(origin.city) === cityKey(place.city))
        ),
      }
    : undefined;
  return (
    <>
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-gray-900">Who&apos;s coming, and from where</h3>
        <p className="text-xs font-medium text-gray-600">
          Fares are priced from each home city for that many people. Paste a list like “San Diego 8, Austin 7”.
        </p>
      </div>

      <ul className="flex list-none flex-col gap-2">
        {workspace.origins.map((origin, index) => (
          <TravelerRow
            key={originKey(origin)}
            place={{
              city: origin.city,
              group: origin.group,
              airport: workspace.airports[cityKey(origin.city)] ?? null,
            }}
            count={origin.count}
            actions={{
              onPick: (place) => pickFor(index, place),
              onCount: (count) => edit({ type: "setOriginCount", key: originKey(origin), count }, "user"),
              onRemove: () => {
                removeOrigin(origin);
                // The last home city gone leaves an empty row to type the next one in.
                if (workspace.origins.length === 1 && drafts.length === 0 && unknown === 0) addDraft();
              },
            }}
            combobox={{
              autoFocus: focusId === `city:${cityKey(origin.city)}`,
              onPasteList: pasteList(null),
              onEnterEmpty: index === workspace.origins.length - 1 && drafts.length === 0 ? addDraft : undefined,
            }}
          />
        ))}
        {drafts.map((draft, index) => (
          <TravelerRow
            key={draft.id}
            place={{ city: null }}
            count={draft.count}
            actions={{
              onPick: (place) => pickDraft(draft, place),
              onCount: (count) =>
                setDrafts((current) => current.map((entry) => (entry.id === draft.id ? { ...entry, count } : entry))),
              onRemove: () => setDrafts((current) => current.filter((entry) => entry.id !== draft.id)),
            }}
            combobox={{
              autoFocus: focusId === draft.id,
              onPasteList: pasteList(draft.id),
              quickPicks,
              draftText: draft.text,
              onDraftText: (text) =>
                setDrafts((current) => current.map((entry) => (entry.id === draft.id ? { ...entry, text } : entry))),
              onBackspaceEmpty:
                workspace.origins.length + drafts.length > 1
                  ? () => setDrafts((current) => current.filter((entry) => entry.id !== draft.id))
                  : undefined,
              onEnterEmpty: index === drafts.length - 1 ? addDraft : undefined,
            }}
          />
        ))}
        {unknown > 0 && (
          <TravelerRow
            place={{ city: null }}
            count={unknown}
            labels={{
              placeholder: "Where are they coming from?",
              emptyLabel: "people without a home city",
              countLabel: "People without a home city",
            }}
            actions={{
              onPick: (place) => {
                // The row becomes that city with its count; the total stays the same.
                setOrigins([...workspace.origins, { city: place.city, count: unknown }]);
                pinAirport(place);
              },
              onCount: (count) => edit({ type: "setHeadcount", headcount: known + count }, "user"),
              onRemove: () => {
                edit({ type: "setHeadcount", headcount: known }, "user");
                if (workspace.origins.length === 0) addDraft();
              },
            }}
            combobox={{
              autoFocus: focusId === null,
              onPasteList: pasteList(null),
              quickPicks,
            }}
          />
        )}
      </ul>

      {unknown > 0 && (
        <p className="text-xs font-medium text-gray-600">
          {people(unknown)} without a home city. Add cities to price their flights.
        </p>
      )}
      {resolving && <p className="text-xs font-medium text-gray-600">Finding those cities…</p>}
      {notFound.length > 0 && (
        <p role="alert" className="text-xs font-medium text-red-600">
          Couldn&apos;t find {notFound.join(", ")}. Add {notFound.length === 1 ? "it" : "them"} from the list.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <FetchButton variant="text" size="sm" color="primary" type="button" onClick={addDraft}>
          <Plus className="size-4" />
          Add home city
        </FetchButton>
        {workspace.origins.length === 0 && unknown === 0 && (
          <FetchButton
            variant="text"
            size="sm"
            color="neutral"
            type="button"
            onClick={() => {
              setDrafts([]);
              edit({ type: "setHeadcount", headcount: 1 }, "user");
            }}
          >
            Just a headcount, cities later
          </FetchButton>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <span className="text-sm font-semibold tabular-nums text-gray-900">Total {people(total)}</span>
        <FetchButton
          variant="outline"
          size="sm"
          color="neutral"
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={reading}
        >
          {workspace.attendeeFileName ? <FileText className="size-4" /> : <Upload className="size-4" />}
          {workspace.attendeeFileName ? (
            <span className="max-w-[180px] truncate" title={`Replace ${workspace.attendeeFileName}`}>
              {workspace.attendeeFileName}
            </span>
          ) : (
            "Upload attendee list"
          )}
        </FetchButton>
        <input
          ref={fileInput}
          type="file"
          accept={ATTENDEE_FILE_ACCEPT}
          className="hidden"
          onChange={(event) => {
            void handleFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </div>
      <AttendeeUploadHint />
    </>
  );
};
